// QrScannerOverlay.tsx — sovelluksen sisäinen QR-skanneri (25.6.2026)
// iOS Safari/WebKit ei tue BarcodeDetectoria → käytetään jsQR:ää kameran ruuduille.
// Skannattu kappaletarra (URL .../kappale/<ref>) → navigoidaan SOVELLUKSEN sisällä
// /kappale/<ref>-sivulle (pysyy PWA:ssa, kirjautuneena — ei hyppää Safariin).
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';

// onScan annettuna → palauttaa skannatun refin callbackilla (esim. NSS "skannaa aloittaaksesi").
// Ilman onScania → navigoi /kappale/<ref>:een (oletus, taaksepäin yhteensopiva).
export default function QrScannerOverlay({ onClose, onScan }: { onClose: () => void; onScan?: (ref: string) => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number>(0);
  const streamRef = useRef<MediaStream | null>(null);
  const doneRef = useRef(false);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // Callbackit refeihin → kamera alustetaan VAIN KERRAN (deps []). Aiemmin effect riippui
  // onClose:sta: jos vanhempi antoi sen inline-muotona ja re-renderöityi, kamera suljettiin
  // ja avattiin jatkuvasti (välkyntä + "ei voitu avata" + lupakysely toistui).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    let cancelled = false;

    const stop = () => {
      cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    };

    const loop = () => {
      const v = videoRef.current;
      if (!v || doneRef.current) return;
      if (v.readyState === v.HAVE_ENOUGH_DATA && v.videoWidth && v.videoHeight) {
        const canvas = canvasRef.current ?? (canvasRef.current = document.createElement('canvas'));
        canvas.width = v.videoWidth;
        canvas.height = v.videoHeight;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (ctx) {
          ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
          const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(img.data, canvas.width, canvas.height, { inversionAttempts: 'dontInvert' });
          const m = code?.data?.match(/\/kappale\/((?:pp|pr|sp)_[0-9a-f-]{36})/i);
          if (m) {
            doneRef.current = true;
            stop();
            if (onScanRef.current) onScanRef.current(m[1]);
            else navigateRef.current(`/kappale/${m[1]}`);
            onCloseRef.current();
            return;
          }
        }
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } }, audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        v.setAttribute('playsinline', 'true');
        await v.play();
        rafRef.current = requestAnimationFrame(loop);
      } catch (e: any) {
        setError(
          e?.name === 'NotAllowedError'
            ? 'Kameran käyttö estetty. Salli kamera selaimen/sovelluksen asetuksista.'
            : 'Kameraa ei voitu avata tällä laitteella.',
        );
      }
    };

    start();
    return () => { cancelled = true; doneRef.current = true; stop(); };
    // Tyhjä deps: kamera alustetaan vain kerran (callbackit refeissä yllä).
     
  }, []);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: '#000', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', color: '#fff', paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))' }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>📷 Skannaa kappaleen QR</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>✕</button>
      </div>
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ width: '64%', maxWidth: 280, aspectRatio: '1 / 1', border: '3px solid rgba(255,255,255,0.85)', borderRadius: 16, boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)' }} />
        </div>
      </div>
      {error
        ? <div style={{ padding: 16, color: '#fff', background: '#7f1d1d', textAlign: 'center', fontSize: 13 }}>{error}</div>
        : <div style={{ padding: '14px 16px calc(14px + env(safe-area-inset-bottom, 0px))', color: '#cbd5e1', textAlign: 'center', fontSize: 13 }}>Suuntaa kamera kappaletarran QR-koodiin</div>}
    </div>
  );
}
